"use client";

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardBody,
  Text,
  Badge,
  HStack,
  VStack,
  Flex,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  useToast,
  Spinner,
  Avatar,
  Icon,
  IconButton,
  StatHelpText,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { FiMessageSquare, FiMail, FiPause, FiPlay } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot, faPhone } from "@fortawesome/free-solid-svg-icons";

import {
  agentService,
  type Agent,
  type VoiceDetails,
  type LLMData,
} from "../voice-services/agentService";
import { phoneNumberService } from "../voice-services/phoneNumberService";
import {
  getDisplayPhonePretty,
  getDisplayPhonePrettyFromFields,
} from "../voice-services/voicePhoneDisplay";
import {
  listInboundAgentIds,
  listOutboundAgentIds,
} from "../voice-services/phoneNumberAgents";

import { smsAgentService, type SMSAgent } from "../sms-services/agentService";
import {
  smsPhoneNumberService,
  type SMSPhoneNumber,
} from "../sms-services/smsPhoneNumberService";

import {
  whatsappAgentService,
  type WhatsAppAgent,
} from "../whatsapp-services/agentService";
import {
  whatsappPhoneNumberService,
  type WhatsAppPhoneNumber,
} from "../whatsapp-services/whatsappPhoneNumberService";

import {
  emailAgentService,
  type EmailAgent,
} from "../email-services/agentService";
import {
  emailAddressService,
  type EmailAddress,
} from "../email-services/emailAddressService";

import GenderImage from "./voice-agents/voices/GenderImages";
import { useAuth } from "../contexts/AuthContext";
import { useStableCredits } from "../hooks/useStableCredits";
import { supabase } from "../supabaseClient";

const MotionBox = motion(Box);

type LoadingFlags = {
  voiceAgents: boolean;
  smsAgents: boolean;
  whatsappAgents: boolean;
  emailAgents: boolean;

  voicePhoneNumbers: boolean;
  smsPhoneNumbers: boolean;
  whatsappPhoneNumbers: boolean;
  emailAddresses: boolean;

  metrics: boolean;

  voiceDetails: boolean;
  llmData: boolean;
};

type VoiceStats = {
  totalAgents: number;
  activeAgents: number;
  totalInteractions: number;
};
type BasicStats = {
  totalAgents: number;
  activeAgents: number;
  totalInteractions: number;
};

type UnifiedAgent =
  | { kind: "voice"; agent: Agent }
  | { kind: "sms"; agent: SMSAgent }
  | { kind: "whatsapp"; agent: WhatsAppAgent }
  | { kind: "email"; agent: EmailAgent };

const AgentsPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const credits = useStableCredits();
  const { isAdmin, currentClientId, clients, userProfile } = useAuth();

  const location = useLocation();

  // Always show "All" when this page is loaded directly
  const [selectedAgentType, setSelectedAgentType] = useState<string>(() => {
    return location.pathname === "/all-agents"
      ? "All"
      : localStorage.getItem("currentAgentType") || "All";
  });

  const [voiceAgents, setVoiceAgents] = useState<Agent[]>([]);
  const [smsAgents, setSmsAgents] = useState<SMSAgent[]>([]);
  const [whatsappAgents, setWhatsappAgents] = useState<WhatsAppAgent[]>([]);
  const [emailAgents, setEmailAgents] = useState<EmailAgent[]>([]);

  const [voicePhonesByAgent, setVoicePhonesByAgent] = useState<
    Record<string, string[]>
  >({});
  const [smsPhoneNumbers, setSmsPhoneNumbers] = useState<SMSPhoneNumber[]>([]);
  const [whatsappPhoneNumbers, setWhatsappPhoneNumbers] = useState<
    WhatsAppPhoneNumber[]
  >([]);
  const [emailAddresses, setEmailAddresses] = useState<EmailAddress[]>([]);

  const [voiceDetails, setVoiceDetails] = useState<
    Record<string, VoiceDetails>
  >({});
  const [llmData, setLlmData] = useState<Record<string, LLMData>>({});

  const [voiceStats, setVoiceStats] = useState<VoiceStats>({
    totalAgents: 0,
    activeAgents: 0,
    totalInteractions: 0,
  });
  const [smsStats, setSmsStats] = useState<BasicStats>({
    totalAgents: 0,
    activeAgents: 0,
    totalInteractions: 0,
  });
  const [whatsappStats, setWhatsappStats] = useState<BasicStats>({
    totalAgents: 0,
    activeAgents: 0,
    totalInteractions: 0,
  });
  const [emailStats, setEmailStats] = useState<BasicStats>({
    totalAgents: 0,
    activeAgents: 0,
    totalInteractions: 0,
  });

  const [pausingAgents, setPausingAgents] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState<LoadingFlags>({
    voiceAgents: true,
    smsAgents: true,
    whatsappAgents: true,
    emailAgents: true,

    voicePhoneNumbers: true,
    smsPhoneNumbers: true,
    whatsappPhoneNumbers: true,
    emailAddresses: true,

    metrics: true,

    voiceDetails: true,
    llmData: true,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const disabled = credits !== null && credits < 0.01;

  const companyName = useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient
      ? currentClient.company_name
      : userProfile?.company_name;
  }, [isAdmin, currentClientId, clients, userProfile]);

  useEffect(() => {
    if (location.pathname !== "/all-agents") return;

    if (selectedAgentType !== "All") {
      setSelectedAgentType("All");
    }
    localStorage.setItem("currentAgentType", "All");
  }, [location.pathname, selectedAgentType]);

  const getVoiceAgentType = (agent: any, llmData: Record<string, any>) => {
    const engineType = agent?.response_engine?.type;

    // Custom LLM
    if (engineType === "custom-llm") {
      return { agentTypeLabel: "Custom LLM", agentTypeColor: "cyan" };
    }

    // Retell LLM
    const llm =
      engineType === "retell-llm"
        ? llmData?.[agent?.response_engine?.llm_id]
        : null;

    const hasStates = Array.isArray(llm?.states) && llm.states.length > 0;

    if (hasStates) {
      return { agentTypeLabel: "Multi-Prompt", agentTypeColor: "blue" };
    }

    return { agentTypeLabel: "Single-Prompt", agentTypeColor: "purple" };
  };

  const allAgents: UnifiedAgent[] = useMemo(() => {
    const list: UnifiedAgent[] = [];
    voiceAgents.forEach((a) => list.push({ kind: "voice", agent: a }));
    smsAgents.forEach((a) => list.push({ kind: "sms", agent: a }));
    whatsappAgents.forEach((a) => list.push({ kind: "whatsapp", agent: a }));
    emailAgents.forEach((a) => list.push({ kind: "email", agent: a }));
    return list;
  }, [voiceAgents, smsAgents, whatsappAgents, emailAgents]);

  const aggregatedTopStats = useMemo(() => {
    // Calculate totalAgents and activeAgents directly from loaded agents (always accurate)
    const totalAgents = allAgents.length;
    const activeAgents = allAgents.filter((item) => {
      const a = item.agent as any;
      return a.is_active !== false; // treat undefined as active (backward compat)
    }).length;

    // totalInteractions still comes from stats APIs (call counts)
    const totalInteractions =
      (voiceStats.totalInteractions || 0) +
      (smsStats.totalInteractions || 0) +
      (whatsappStats.totalInteractions || 0) +
      (emailStats.totalInteractions || 0);

    return { totalAgents, activeAgents, totalInteractions };
  }, [allAgents, voiceStats, smsStats, whatsappStats, emailStats]);

  const fetchAllPageData = useCallback(async () => {
    // Abort inflight
    if (abortControllerRef.current) abortControllerRef.current.abort();

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // ✅ Standard: revalidate without blanking UI
    setLoading((prev) => ({
      ...prev,
      voiceAgents: true,
      smsAgents: true,
      whatsappAgents: true,
      emailAgents: true,

      voicePhoneNumbers: true,
      smsPhoneNumbers: true,
      whatsappPhoneNumbers: true,
      emailAddresses: true,

      metrics: true,

      // Details load after voice agents; keep existing maps
      voiceDetails: false,
      llmData: false,
    }));

    try {
      // Keep supabase usage (do not remove)
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        console.warn("No active Supabase session (AgentsPage).");
      }

      // ---- Agents (progressive) ----
      void agentService
        .list(companyName)
        .then((fresh) => {
          if (signal.aborted) return;
          const map = new Map<string, Agent>();
          (fresh || []).forEach((a: Agent) => map.set(a.agent_id, a));
          const unique = Array.from(map.values());
          setVoiceAgents(unique);

          // voice details + llm data (progressive, abort-safe)
          void (async () => {
            try {
              setLoading((p) => ({ ...p, voiceDetails: true, llmData: true }));

              const uniqueVoiceIds = Array.from(
                new Set(unique.map((a) => a.voice_id).filter(Boolean)),
              );

              if (uniqueVoiceIds.length > 0) {
                const batchSize = 6;
                for (let i = 0; i < uniqueVoiceIds.length; i += batchSize) {
                  if (signal.aborted) return;
                  const batch = uniqueVoiceIds.slice(i, i + batchSize);
                  const batchResults = await Promise.all(
                    batch.map((id) => agentService.getVoiceDetails(id)),
                  );
                  if (signal.aborted) return;

                  const partial: Record<string, VoiceDetails> = {};
                  batch.forEach((id, idx) => (partial[id] = batchResults[idx]));
                  setVoiceDetails((prev) => ({ ...prev, ...partial }));
                }
              }

              const uniqueLlmIds = Array.from(
                new Set(
                  unique
                    .map((a) =>
                      a.response_engine?.type === "retell-llm"
                        ? a.response_engine.llm_id
                        : null,
                    )
                    .filter(Boolean) as string[],
                ),
              );

              if (uniqueLlmIds.length > 0) {
                const batchSize = 6;
                for (let i = 0; i < uniqueLlmIds.length; i += batchSize) {
                  if (signal.aborted) return;
                  const batch = uniqueLlmIds.slice(i, i + batchSize);
                  const batchResults = await Promise.all(
                    batch.map((id) => agentService.getLLMData(id)),
                  );
                  if (signal.aborted) return;

                  const partial: Record<string, LLMData> = {};
                  batch.forEach((id, idx) => (partial[id] = batchResults[idx]));
                  setLlmData((prev) => ({ ...prev, ...partial }));
                }
              }
            } catch (e: any) {
              if (e?.name === "AbortError") return;
              console.error("Error loading voice/llm details:", e);
            } finally {
              if (!signal.aborted) {
                setLoading((p) => ({
                  ...p,
                  voiceDetails: false,
                  llmData: false,
                }));
              }
            }
          })();
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          console.error("Error loading Voice agents:", e);
        })
        .finally(() => {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, voiceAgents: false }));
        });

      void smsAgentService
        .list(companyName)
        .then((fresh) => {
          if (signal.aborted) return;
          const map = new Map<string, SMSAgent>();
          (fresh || []).forEach((a: SMSAgent) => map.set(a.agent_id, a));
          setSmsAgents(Array.from(map.values()));
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          console.error("Error loading SMS agents:", e);
        })
        .finally(() => {
          if (!signal.aborted) setLoading((p) => ({ ...p, smsAgents: false }));
        });

      void whatsappAgentService
        .list(companyName)
        .then((fresh) => {
          if (signal.aborted) return;
          const map = new Map<string, WhatsAppAgent>();
          (fresh || []).forEach((a: WhatsAppAgent) => map.set(a.agent_id, a));
          setWhatsappAgents(Array.from(map.values()));
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          console.error("Error loading WhatsApp agents:", e);
        })
        .finally(() => {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, whatsappAgents: false }));
        });

      void emailAgentService
        .list(companyName)
        .then((fresh) => {
          if (signal.aborted) return;
          const map = new Map<string, EmailAgent>();
          (fresh || []).forEach((a: EmailAgent) => map.set(a.agent_id, a));
          setEmailAgents(Array.from(map.values()));
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          console.error("Error loading Email agents:", e);
        })
        .finally(() => {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, emailAgents: false }));
        });

      // ---- Phone numbers / addresses (progressive) ----
      void phoneNumberService
        .list()
        .then((numbers: any[]) => {
          if (signal.aborted) return;
          const agentPhoneMap: Record<string, string[]> = {};
          (numbers || []).forEach((n: any) => {
            const display = getDisplayPhonePretty(n);
            listInboundAgentIds(n).forEach((id) => {
              (agentPhoneMap[id] ??= []).push(display);
            });
            listOutboundAgentIds(n).forEach((id) => {
              (agentPhoneMap[id] ??= []).push(display);
            });
          });
          setVoicePhonesByAgent(agentPhoneMap);
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          console.error("Error loading Voice phone numbers:", e);
        })
        .finally(() => {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, voicePhoneNumbers: false }));
        });

      void smsPhoneNumberService
        .list()
        .then((numbers: SMSPhoneNumber[]) => {
          if (signal.aborted) return;
          setSmsPhoneNumbers(numbers || []);
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          console.error("Error loading SMS phone numbers:", e);
        })
        .finally(() => {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, smsPhoneNumbers: false }));
        });

      void whatsappPhoneNumberService
        .list()
        .then((numbers: WhatsAppPhoneNumber[]) => {
          if (signal.aborted) return;
          setWhatsappPhoneNumbers(numbers || []);
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          // Silently handle 404 if endpoint not implemented
          if (e?.response?.status !== 404)
            console.error("Error loading WhatsApp phone numbers:", e);
        })
        .finally(() => {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, whatsappPhoneNumbers: false }));
        });

      void emailAddressService
        .list(companyName)
        .then((addresses: EmailAddress[]) => {
          if (signal.aborted) return;
          setEmailAddresses(addresses || []);
        })
        .catch((e: any) => {
          if (e?.name === "AbortError") return;
          console.error("Error loading Email addresses:", e);
        })
        .finally(() => {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, emailAddresses: false }));
        });

      // ---- Metrics (progressive; NO caching; NO call list) ----
      void (async () => {
        try {
          // Voice stats uses Bearer user id (same as your voice page)
          if (session?.user?.id) {
            const url = new URL(
              `${process.env.REACT_APP_SERVER_URL}/api/voice-agents/stats`,
            );
            if (companyName)
              url.searchParams.append("company_name", companyName);

            const res = await fetch(url.toString(), {
              headers: { Authorization: `Bearer ${session.user.id}` },
              signal,
            });

            if (!signal.aborted && res.ok) setVoiceStats(await res.json());
          }

          // SMS / WhatsApp / Email stats use Bearer currentClientId in your other pages
          const [smsRes, waRes, emailRes] = await Promise.allSettled([
            (async () => {
              const url = new URL(
                `${process.env.REACT_APP_SERVER_URL}/api/sms-agents/stats`,
              );
              if (companyName)
                url.searchParams.append("company_name", companyName);
              const res = await fetch(url.toString(), {
                headers: { Authorization: `Bearer ${currentClientId}` },
                signal,
              });
              if (!signal.aborted && res.ok) setSmsStats(await res.json());
            })(),
            (async () => {
              const url = new URL(
                `${process.env.REACT_APP_SERVER_URL}/api/whatsapp-agents/stats`,
              );
              if (companyName)
                url.searchParams.append("company_name", companyName);
              const res = await fetch(url.toString(), {
                headers: { Authorization: `Bearer ${currentClientId}` },
                signal,
              });
              if (!signal.aborted && res.ok) setWhatsappStats(await res.json());
            })(),
            (async () => {
              const url = new URL(
                `${process.env.REACT_APP_SERVER_URL}/api/email-agents/stats`,
              );
              if (companyName)
                url.searchParams.append("company_name", companyName);
              const res = await fetch(url.toString(), {
                headers: { Authorization: `Bearer ${currentClientId}` },
                signal,
              });
              if (!signal.aborted && res.ok) setEmailStats(await res.json());
            })(),
          ]);

          // ignore individual failures; keep defaults
          void smsRes;
          void waRes;
          void emailRes;
        } catch (e: any) {
          if (e?.name === "AbortError") return;
          console.error("Error loading aggregated metrics:", e);
        } finally {
          if (!signal.aborted) setLoading((p) => ({ ...p, metrics: false }));
        }
      })();
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      console.error("Error loading AgentsPage:", e);
      toast({ title: "Error loading agents", status: "error", duration: 3000 });
      if (!signal.aborted) {
        setLoading({
          voiceAgents: false,
          smsAgents: false,
          whatsappAgents: false,
          emailAgents: false,
          voicePhoneNumbers: false,
          smsPhoneNumbers: false,
          whatsappPhoneNumbers: false,
          emailAddresses: false,
          metrics: false,
          voiceDetails: false,
          llmData: false,
        });
      }
    }
  }, [companyName, currentClientId, toast]);

  // Tenant switch: abort + clear UI (no caching)
  useEffect(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();

    setVoiceAgents([]);
    setSmsAgents([]);
    setWhatsappAgents([]);
    setEmailAgents([]);

    setVoicePhonesByAgent({});
    setSmsPhoneNumbers([]);
    setWhatsappPhoneNumbers([]);
    setEmailAddresses([]);

    setVoiceDetails({});
    setLlmData({});

    setVoiceStats({ totalAgents: 0, activeAgents: 0, totalInteractions: 0 });
    setSmsStats({ totalAgents: 0, activeAgents: 0, totalInteractions: 0 });
    setWhatsappStats({ totalAgents: 0, activeAgents: 0, totalInteractions: 0 });
    setEmailStats({ totalAgents: 0, activeAgents: 0, totalInteractions: 0 });

    setLoading({
      voiceAgents: true,
      smsAgents: true,
      whatsappAgents: true,
      emailAgents: true,
      voicePhoneNumbers: true,
      smsPhoneNumbers: true,
      whatsappPhoneNumbers: true,
      emailAddresses: true,
      metrics: true,
      voiceDetails: true,
      llmData: true,
    });
  }, [companyName]);

  useEffect(() => {
    fetchAllPageData();
    return () => abortControllerRef.current?.abort();
  }, [fetchAllPageData]);

  // keep fresh after admin/client changes (progressive)
  useEffect(() => {
    fetchAllPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, currentClientId]);

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);

    if (type === "Voice") navigate("/voice-agents");
    else if (type === "SMS") navigate("/sms-agents");
    else if (type === "Email") navigate("/email-agents");
    else if (type === "WhatsApp") navigate("/whatsapp-agents");
    else navigate("/all-agents");
  };

  const handleDelete = async (kind: UnifiedAgent["kind"], agentId: string) => {
    try {
      if (kind === "voice") await agentService.delete(agentId);
      if (kind === "sms") await smsAgentService.delete(agentId);
      if (kind === "whatsapp") await whatsappAgentService.delete(agentId);
      if (kind === "email") await emailAgentService.delete(agentId);

      toast({
        title: "Agent deleted successfully",
        status: "success",
        duration: 3000,
      });
      fetchAllPageData();
    } catch (e) {
      toast({ title: "Error deleting agent", status: "error", duration: 3000 });
    }
  };

  const handlePauseResume = async (
    kind: UnifiedAgent["kind"],
    agentId: string,
    currentlyActive: boolean,
  ) => {
    setPausingAgents((prev) => new Set(prev).add(agentId));
    try {
      const action = currentlyActive ? "pause" : "resume";
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/agents/${kind}/${agentId}/${action}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "user-id": (await supabase.auth.getSession()).data.session?.user.id || "",
          },
        },
      );
      if (!res.ok) throw new Error(await res.text());

      toast({
        title: currentlyActive ? "Agent paused" : "Agent resumed",
        status: "success",
        duration: 2000,
      });

      // Optimistic update in local state
      if (kind === "voice") {
        setVoiceAgents((prev) =>
          prev.map((a) =>
            a.agent_id === agentId ? { ...a, is_active: !currentlyActive } : a,
          ),
        );
      } else if (kind === "sms") {
        setSmsAgents((prev) =>
          prev.map((a) =>
            a.agent_id === agentId ? { ...a, is_active: !currentlyActive } : a,
          ),
        );
      } else if (kind === "whatsapp") {
        setWhatsappAgents((prev) =>
          prev.map((a: any) =>
            a.agent_id === agentId ? { ...a, is_active: !currentlyActive } : a,
          ),
        );
      } else if (kind === "email") {
        setEmailAgents((prev) =>
          prev.map((a: any) =>
            a.agent_id === agentId ? { ...a, is_active: !currentlyActive } : a,
          ),
        );
      }
    } catch (e) {
      toast({
        title: "Error updating agent status",
        status: "error",
        duration: 3000,
      });
    } finally {
      setPausingAgents((prev) => {
        const next = new Set(prev);
        next.delete(agentId);
        return next;
      });
    }
  };

  const renderAgentCard = (item: UnifiedAgent) => {
    const cardDisabled = disabled;

    if (item.kind === "voice") {
      const a = item.agent;
      const voice = voiceDetails[a.voice_id];
      const phones = voicePhonesByAgent[a.agent_id] || [];
      const hasPhone = phones.length > 0;
      const primaryPhone = phones[0];
      const isActive = a.is_active !== false; // default true if undefined
      const isPausing = pausingAgents.has(a.agent_id);

      const { agentTypeLabel, agentTypeColor } = getVoiceAgentType(a, llmData);

      return (
        <Card
          key={`voice:${a.agent_id}`}
          role="button"
          tabIndex={cardDisabled ? -1 : 0}
          aria-disabled={cardDisabled}
          borderLeft="4px"
          borderLeftColor="primary.500"
          cursor={cardDisabled ? "not-allowed" : "pointer"}
          opacity={cardDisabled ? 0.55 : 1}
          onClick={() => {
            if (cardDisabled) return;
            navigate(`/voice-agents/${a.agent_id}`);
          }}
          _hover={cardDisabled ? {} : { shadow: "md" }}
          transition="all 0.2s"
          minH="250px"
          display="flex"
          flexDirection="column"
          title={
            cardDisabled
              ? "Insufficient balance to view agent details"
              : undefined
          }
        >
          <CardBody display="flex" flexDirection="column" p={5}>
            <VStack align="stretch" spacing={3} flex={1}>
              {/* Header */}
              <Flex justify="space-between" align="start" gap={3}>
                <HStack align="start" spacing={3} flex={1} minW={0}>
                  {/* Avatar (52px + outline) */}
                  <Box
                    w="52px"
                    h="52px"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="primary.500"
                    overflow="hidden"
                    flexShrink={0}
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {voice ? (
                      <GenderImage voice={voice} boxSize="32px" mr={0} />
                    ) : loading.voiceDetails ? (
                      <Spinner size="sm" />
                    ) : (
                      <Avatar size="sm" bg="gray.200" />
                    )}
                  </Box>

                  <VStack align="start" spacing={1} flex={1} minW={0}>
                    <Text
                      fontSize="lg"
                      fontWeight="600"
                      color="black"
                      noOfLines={2}
                      lineHeight="1.2"
                    >
                      {a.agent_name || "Unnamed Agent"}
                    </Text>

                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme="purple" fontSize="xs">
                        VOICE
                      </Badge>

                      <Badge
                        colorScheme={isActive ? "green" : "yellow"}
                        fontSize="xs"
                      >
                        {isActive ? "ACTIVE" : "PAUSED"}
                      </Badge>
                    </HStack>
                  </VStack>
                </HStack>

                {/* Actions */}
                <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    aria-label={isActive ? "Pause agent" : "Resume agent"}
                    icon={<Icon as={isActive ? FiPause : FiPlay} />}
                    size="sm"
                    variant="ghost"
                    colorScheme={isActive ? "yellow" : "green"}
                    isLoading={isPausing}
                    onClick={() => handlePauseResume("voice", a.agent_id, isActive)}
                    title={isActive ? "Pause agent" : "Resume agent"}
                  />
                  <IconButton
                    aria-label="Edit agent"
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/voice-agents/${a.agent_id}`)}
                    isDisabled={cardDisabled}
                    title={
                      cardDisabled
                        ? "Insufficient balance to edit agent"
                        : "Edit"
                    }
                  />
                  <IconButton
                    aria-label="Delete agent"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDelete("voice", a.agent_id)}
                    title="Delete"
                  />
                </HStack>
              </Flex>

              {/* Details */}
              <VStack
                align="stretch"
                spacing={2}
                pt={2}
                borderTop="1px solid"
                borderColor="gray.100"
              >
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600">
                    Phone Number:
                  </Text>

                  {loading.voicePhoneNumbers ? (
                    <Spinner size="xs" />
                  ) : hasPhone ? (
                    <Badge
                      colorScheme="green"
                      fontSize="xs"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      <FontAwesomeIcon icon={faPhone} />
                      <Text as="span">{primaryPhone}</Text>
                    </Badge>
                  ) : (
                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                      Not connected
                    </Text>
                  )}
                </Flex>

                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600">
                    Voice:
                  </Text>

                  {loading.voiceDetails ? (
                    <Spinner size="xs" />
                  ) : (
                    <Badge
                      bg="pink.200"
                      color="gray.800"
                      fontSize="xs"
                      fontWeight="600"
                      px={2}
                      py={1}
                      borderRadius="md"
                      maxW="260px"
                      noOfLines={1}
                    >
                      {voice?.voice_name || "—"}
                    </Badge>
                  )}
                </Flex>
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600">
                    Agent Type:
                  </Text>

                  {loading.llmData ? (
                    <Spinner size="xs" />
                  ) : (
                    <Badge colorScheme={agentTypeColor} fontSize="xs">
                      {agentTypeLabel}
                    </Badge>
                  )}
                </Flex>
              </VStack>

              {/* Footer */}
              <Flex
                pt={2}
                mt="auto"
                borderTop="1px solid"
                borderColor="gray.100"
                justify="flex-end"
              >
                <Text fontSize="xs" color="gray.500">
                  Last Updated:{" "}
                  {new Date(a.last_modification_timestamp).toLocaleString(
                    "en-US",
                    {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    },
                  )}
                </Text>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    if (item.kind === "sms") {
      const a = item.agent;
      const phoneObj =
        smsPhoneNumbers.find((p) => p.agent_id === a.agent_id) || null;
      const canonical = phoneObj
        ? (
            phoneObj.phone_number ||
            (phoneObj as { number?: string }).number ||
            ""
          ).trim()
        : "";
      const prettyRaw = phoneObj
        ? (phoneObj.phone_number_pretty || "").trim()
        : "";
      const phonePretty = phoneObj
        ? getDisplayPhonePrettyFromFields({
            phone_number: canonical || prettyRaw,
            phone_number_pretty: prettyRaw || null,
          }) || null
        : null;
      const hasPhone = !!phonePretty;
      const isActive = a.is_active !== false;
      const isPausing = pausingAgents.has(a.agent_id);

      const agentTypeLabel = "Single-Prompt";
      const agentTypeColor = "purple";

      return (
        <Card
          key={`sms:${a.agent_id}`}
          role="button"
          tabIndex={cardDisabled ? -1 : 0}
          aria-disabled={cardDisabled}
          borderLeft="4px"
          borderLeftColor="blue.500"
          cursor={cardDisabled ? "not-allowed" : "pointer"}
          opacity={cardDisabled ? 0.55 : 1}
          onClick={() => {
            if (cardDisabled) return;
            navigate(`/sms-agents/${a.agent_id}`);
          }}
          _hover={cardDisabled ? {} : { shadow: "md" }}
          transition="all 0.2s"
          minH="250px"
          display="flex"
          flexDirection="column"
          title={
            cardDisabled
              ? "Insufficient balance to view agent details"
              : undefined
          }
        >
          <CardBody display="flex" flexDirection="column" p={5}>
            <VStack align="stretch" spacing={3} flex={1}>
              {/* Header */}
              <Flex justify="space-between" align="start" gap={3}>
                <HStack align="start" spacing={3} flex={1} minW={0}>
                  <Box
                    w="52px"
                    h="52px"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="blue.500"
                    overflow="hidden"
                    flexShrink={0}
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Avatar
                      size="sm"
                      bg="blue.500"
                      icon={
                        <Icon
                          as={FiMessageSquare}
                          color="white"
                          fontSize="1.05rem"
                        />
                      }
                    />
                  </Box>

                  <VStack align="start" spacing={1} flex={1} minW={0}>
                    <Text
                      fontSize="lg"
                      fontWeight="600"
                      color="black"
                      noOfLines={2}
                      lineHeight="1.2"
                    >
                      {a.name || "Unnamed Agent"}
                    </Text>

                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme="blue" fontSize="xs">
                        SMS
                      </Badge>
                      <Badge
                        colorScheme={isActive ? "green" : "yellow"}
                        fontSize="xs"
                      >
                        {isActive ? "ACTIVE" : "PAUSED"}
                      </Badge>
                    </HStack>
                  </VStack>
                </HStack>

                {/* Actions */}
                <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    aria-label={isActive ? "Pause agent" : "Resume agent"}
                    icon={<Icon as={isActive ? FiPause : FiPlay} />}
                    size="sm"
                    variant="ghost"
                    colorScheme={isActive ? "yellow" : "green"}
                    isLoading={isPausing}
                    onClick={() => handlePauseResume("sms", a.agent_id, isActive)}
                    title={isActive ? "Pause agent" : "Resume agent"}
                  />
                  <IconButton
                    aria-label="Edit agent"
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/sms-agents/${a.agent_id}`)}
                    isDisabled={cardDisabled}
                    title={
                      cardDisabled
                        ? "Insufficient balance to edit agent"
                        : "Edit"
                    }
                  />
                  <IconButton
                    aria-label="Delete agent"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDelete("sms", a.agent_id)}
                    title="Delete"
                  />
                </HStack>
              </Flex>

              {/* Details */}
              <VStack
                align="stretch"
                spacing={2}
                pt={2}
                borderTop="1px solid"
                borderColor="gray.100"
              >
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600">
                    Phone Number:
                  </Text>

                  {loading.smsPhoneNumbers ? (
                    <Spinner size="xs" />
                  ) : hasPhone ? (
                    <Badge
                      colorScheme="green"
                      fontSize="xs"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      <FontAwesomeIcon icon={faPhone} />
                      <Text as="span">{phonePretty}</Text>
                    </Badge>
                  ) : (
                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                      Not connected
                    </Text>
                  )}
                </Flex>

                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600">
                    Agent Type:
                  </Text>

                  <Badge colorScheme={agentTypeColor} fontSize="xs">
                    {agentTypeLabel}
                  </Badge>
                </Flex>
              </VStack>

              {/* Footer */}
              <Flex
                pt={2}
                mt="auto"
                borderTop="1px solid"
                borderColor="gray.100"
                justify="flex-end"
              >
                <Text fontSize="xs" color="gray.500">
                  Last Updated:{" "}
                  {new Date((a as any).updated_at).toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </Text>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    if (item.kind === "email") {
      const a = item.agent;

      const assigned = emailAddresses.find(
        (addr: any) => addr.assigned_agent_id === a.agent_id,
      );
      const hasEmail = !!assigned;
      const emailValue =
        (assigned as any)?.email_address ||
        (assigned as any)?.address ||
        (assigned as any)?.email ||
        (assigned as any)?.mailbox ||
        "";
      const isActive = (a as any).is_active !== false;
      const isPausing = pausingAgents.has((a as any).agent_id);

      const agentTypeLabel = "Single-Prompt";
      const agentTypeColor = "purple";

      return (
        <Card
          key={`email:${a.agent_id}`}
          role="button"
          tabIndex={cardDisabled ? -1 : 0}
          aria-disabled={cardDisabled}
          borderLeft="4px"
          borderLeftColor="orange.500"
          cursor={cardDisabled ? "not-allowed" : "pointer"}
          opacity={cardDisabled ? 0.55 : 1}
          onClick={() => {
            if (cardDisabled) return;
            navigate(`/email-agents/${a.agent_id}`);
          }}
          _hover={cardDisabled ? {} : { shadow: "md" }}
          transition="all 0.2s"
          minH="250px"
          display="flex"
          flexDirection="column"
          title={
            cardDisabled
              ? "Insufficient balance to view agent details"
              : undefined
          }
        >
          <CardBody display="flex" flexDirection="column" p={5}>
            <VStack align="stretch" spacing={3} flex={1}>
              {/* Header */}
              <Flex justify="space-between" align="start" gap={3}>
                <HStack align="start" spacing={3} flex={1} minW={0}>
                  <Box
                    w="52px"
                    h="52px"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="orange.500"
                    overflow="hidden"
                    flexShrink={0}
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Avatar
                      size="sm"
                      bg="orange.500"
                      icon={
                        <Icon as={FiMail} color="white" fontSize="1.05rem" />
                      }
                    />
                  </Box>

                  <VStack align="start" spacing={1} flex={1} minW={0}>
                    <Text
                      fontSize="lg"
                      fontWeight="600"
                      color="black"
                      noOfLines={2}
                      lineHeight="1.2"
                    >
                      {a.name || "Unnamed Agent"}
                    </Text>

                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme="orange" fontSize="xs">
                        EMAIL
                      </Badge>
                      <Badge
                        colorScheme={isActive ? "green" : "yellow"}
                        fontSize="xs"
                      >
                        {isActive ? "ACTIVE" : "PAUSED"}
                      </Badge>
                    </HStack>
                  </VStack>
                </HStack>

                {/* Actions */}
                <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    aria-label={isActive ? "Pause agent" : "Resume agent"}
                    icon={<Icon as={isActive ? FiPause : FiPlay} />}
                    size="sm"
                    variant="ghost"
                    colorScheme={isActive ? "yellow" : "green"}
                    isLoading={isPausing}
                    onClick={() => handlePauseResume("email", (a as any).agent_id, isActive)}
                    title={isActive ? "Pause agent" : "Resume agent"}
                  />
                  <IconButton
                    aria-label="Edit agent"
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/email-agents/${(a as any).agent_id}`)}
                    isDisabled={cardDisabled}
                    title={
                      cardDisabled
                        ? "Insufficient balance to edit agent"
                        : "Edit"
                    }
                  />
                  <IconButton
                    aria-label="Delete agent"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDelete("email", (a as any).agent_id)}
                    title="Delete"
                  />
                </HStack>
              </Flex>

              {/* Details */}
              <VStack
                align="stretch"
                spacing={2}
                pt={2}
                borderTop="1px solid"
                borderColor="gray.100"
              >
                <Flex justify="space-between" align="center" gap={3}>
                  <Text fontSize="sm" color="gray.600">
                    Email Address:
                  </Text>

                  {loading.emailAddresses ? (
                    <Spinner size="xs" />
                  ) : hasEmail ? (
                    <Badge
                      colorScheme="green"
                      fontSize="xs"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      <Icon as={FiMail} />
                      <Text as="span">{emailValue}</Text>
                    </Badge>
                  ) : (
                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                      Not connected
                    </Text>
                  )}
                </Flex>

                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600">
                    Agent Type:
                  </Text>

                  <Badge colorScheme={agentTypeColor} fontSize="xs">
                    {agentTypeLabel}
                  </Badge>
                </Flex>
              </VStack>

              {/* Footer */}
              <Flex
                pt={2}
                mt="auto"
                borderTop="1px solid"
                borderColor="gray.100"
                justify="flex-end"
              >
                <Text fontSize="xs" color="gray.500">
                  Last Updated:{" "}
                  {new Date((a as any).updated_at).toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </Text>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    // whatsapp
    if (item.kind !== "whatsapp") return null;
    const a = item.agent;
    const phoneObj =
      whatsappPhoneNumbers.find((p) => p.agent_id === a.agent_id) || null;
    const phonePretty =
      (phoneObj as any)?.phone_number_pretty ||
      (phoneObj as any)?.phone_number ||
      (phoneObj as any)?.number ||
      null;
    const hasPhone = !!phonePretty;
    const isActive = (a as any).is_active !== false;
    const isPausing = pausingAgents.has(a.agent_id);

    const agentTypeLabel = "Single-Prompt";
    const agentTypeColor = "purple";

    return (
      <Card
        key={`whatsapp:${a.agent_id}`}
        role="button"
        tabIndex={cardDisabled ? -1 : 0}
        aria-disabled={cardDisabled}
        borderLeft="4px"
        borderLeftColor="green.500"
        cursor={cardDisabled ? "not-allowed" : "pointer"}
        opacity={cardDisabled ? 0.55 : 1}
        onClick={() => {
          if (cardDisabled) return;
          navigate(`/whatsapp-agents/${a.agent_id}`);
        }}
        _hover={cardDisabled ? {} : { shadow: "md" }}
        transition="all 0.2s"
        minH="250px"
        display="flex"
        flexDirection="column"
        title={
          cardDisabled
            ? "Insufficient balance to view agent details"
            : undefined
        }
      >
        <CardBody display="flex" flexDirection="column" p={5}>
          <VStack align="stretch" spacing={3} flex={1}>
            {/* Header */}
            <Flex justify="space-between" align="start" gap={3}>
              <HStack align="start" spacing={3} flex={1} minW={0}>
                <Box
                  w="52px"
                  h="52px"
                  borderRadius="full"
                  border="2px solid"
                  borderColor="green.500"
                  overflow="hidden"
                  flexShrink={0}
                  bg="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Avatar
                    size="sm"
                    bg="green.500"
                    icon={
                      <Icon as={FaWhatsapp} color="white" fontSize="1.05rem" />
                    }
                  />
                </Box>

                <VStack align="start" spacing={1} flex={1} minW={0}>
                  <Text
                    fontSize="lg"
                    fontWeight="600"
                    color="black"
                    noOfLines={2}
                    lineHeight="1.2"
                  >
                    {(a as any).name || "Unnamed Agent"}
                  </Text>

                  <HStack spacing={2} flexWrap="wrap">
                    <Badge colorScheme="green" fontSize="xs">
                      WHATSAPP
                    </Badge>
                    <Badge
                      colorScheme={isActive ? "green" : "yellow"}
                      fontSize="xs"
                    >
                      {isActive ? "ACTIVE" : "PAUSED"}
                    </Badge>
                  </HStack>
                </VStack>
              </HStack>

              {/* Actions */}
              <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                <IconButton
                  aria-label={isActive ? "Pause agent" : "Resume agent"}
                  icon={<Icon as={isActive ? FiPause : FiPlay} />}
                  size="sm"
                  variant="ghost"
                  colorScheme={isActive ? "yellow" : "green"}
                  isLoading={isPausing}
                  onClick={() => handlePauseResume("whatsapp", a.agent_id, isActive)}
                  title={isActive ? "Pause agent" : "Resume agent"}
                />
                <IconButton
                  aria-label="Edit agent"
                  icon={<EditIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/whatsapp-agents/${a.agent_id}`)}
                  isDisabled={cardDisabled}
                  title={
                    cardDisabled ? "Insufficient balance to edit agent" : "Edit"
                  }
                />
                <IconButton
                  aria-label="Delete agent"
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleDelete("whatsapp", a.agent_id)}
                  title="Delete"
                />
              </HStack>
            </Flex>

            {/* Details */}
            <VStack
              align="stretch"
              spacing={2}
              pt={2}
              borderTop="1px solid"
              borderColor="gray.100"
            >
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">
                  Phone Number:
                </Text>

                {loading.whatsappPhoneNumbers ? (
                  <Spinner size="xs" />
                ) : hasPhone ? (
                  <Badge
                    colorScheme="green"
                    fontSize="xs"
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    <FontAwesomeIcon icon={faPhone} />
                    <Text as="span">{phonePretty}</Text>
                  </Badge>
                ) : (
                  <Text fontSize="sm" fontWeight="600" color="gray.800">
                    Not connected
                  </Text>
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">
                  Agent Type:
                </Text>

                <Badge colorScheme={agentTypeColor} fontSize="xs">
                  {agentTypeLabel}
                </Badge>
              </Flex>
            </VStack>

            {/* Footer */}
            <Flex
              pt={2}
              mt="auto"
              borderTop="1px solid"
              borderColor="gray.100"
              justify="flex-end"
            >
              <Text fontSize="xs" color="gray.500">
                Last Updated:{" "}
                {new Date((a as any).updated_at).toLocaleString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </Text>
            </Flex>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box p={6} bg="white" minH="100vh">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold" color="black">
          AI Agents
        </Text>

        <MotionBox
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          transition="0.15s ease"
        >
          <Select
            value={selectedAgentType}
            onChange={(e) => handleAgentTypeChange(e.target.value)}
            height="48px"
            bg="white"
            border="2px solid"
            borderColor="gray.200"
            borderRadius="12px"
            color="gray.800"
            fontWeight="semibold"
            fontSize="sm"
            pl="12px"
            pr="36px"
            cursor="pointer"
            shadow="sm"
            transition="all 0.2s ease"
            isDisabled={disabled}
            opacity={disabled ? 0.6 : 1}
            title={
              disabled
                ? "Insufficient balance. Please add balance to view agents."
                : undefined
            }
            _hover={{ borderColor: "primary.400", shadow: "md" }}
            _focus={{
              borderColor: "primary.600",
              boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
            }}
          >
            <option value="All">
              🤖 All Agents - Voice, SMS, Email, WhatsApp
            </option>
            <option value="Voice">📞 Voice Agents - Phone calls with AI</option>
            <option value="SMS">💬 SMS Agents - Text messaging with AI</option>

            {process.env.REACT_APP_IS_MAIN_APP === "true" && (
              <option value="Email">
                📧 Email Agents - Email conversations with AI
              </option>
            )}

            <option value="WhatsApp">
              📱 WhatsApp Agents - WhatsApp messaging with AI
            </option>
          </Select>
        </MotionBox>
      </Flex>

      {/* Top metrics (standard: 3 cards, no call aggregation) */}
      {allAgents.length > 0 && (
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap={4}
          mb={6}
        >
          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Agents</StatLabel>
                <StatNumber color="black">
                  {loading.voiceAgents || loading.smsAgents || loading.whatsappAgents || loading.emailAgents ? (
                    <Spinner size="sm" />
                  ) : (
                    aggregatedTopStats.totalAgents
                  )}
                </StatNumber>
                <StatHelpText>Across all channels</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Active Agents</StatLabel>
                <StatNumber color="green.500">
                  {loading.voiceAgents || loading.smsAgents || loading.whatsappAgents || loading.emailAgents ? (
                    <Spinner size="sm" />
                  ) : (
                    aggregatedTopStats.activeAgents
                  )}
                </StatNumber>
                <StatHelpText>Currently running</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Interactions</StatLabel>
                <StatNumber color="black">
                  {loading.metrics ? (
                    <Spinner size="sm" />
                  ) : (
                    aggregatedTopStats.totalInteractions
                  )}
                </StatNumber>
                <StatHelpText>All time</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {/* Empty state (no full-screen gate; show spinner inside box if loading agents) */}
      {allAgents.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          minH="calc(100vh - 80px)"
        >
          {loading.voiceAgents ||
          loading.smsAgents ||
          loading.whatsappAgents ||
          loading.emailAgents ? (
            <Spinner size="xl" color="primary.500" />
          ) : (
            <>
              <FontAwesomeIcon icon={faRobot} color="gray" size="5x" />
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color="gray.700"
                mb={2}
                mt={6}
              >
                No Agents Found!
              </Text>
              <Text
                fontSize="md"
                color="gray.600"
                mb={8}
                textAlign="center"
                maxW="500px"
              >
                Get started by selecting the type of agent you'd like to create
              </Text>

              <VStack spacing={4} width="100%" maxW="400px">
                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                  Select Agent Type
                </Text>
                <Select
                  placeholder="Choose an agent type to create..."
                  onChange={(e) => {
                    const type = e.target.value;
                    if (type === "Voice") navigate("/voice-agents");
                    else if (type === "SMS") navigate("/sms-agents");
                    else if (type === "Email") navigate("/email-agents");
                    else if (type === "WhatsApp") navigate("/whatsapp-agents");
                  }}
                  size="lg"
                  bg="white"
                  borderColor="primary.300"
                  borderWidth="2px"
                  borderRadius="lg"
                  color="gray.700"
                  fontWeight="medium"
                  fontSize="md"
                  isDisabled={disabled}
                  opacity={disabled ? 0.6 : 1}
                  _hover={{
                    borderColor: "primary.500",
                    transform: "translateY(-2px)",
                    shadow: "lg",
                  }}
                  _focus={{
                    borderColor: "primary.600",
                    boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.2)",
                  }}
                  cursor="pointer"
                  transition="all 0.2s"
                >
                  <option value="Voice">
                    📞 Voice Agents - Phone calls with AI
                  </option>
                  <option value="SMS">
                    💬 SMS Agents - Text messaging with AI
                  </option>
                  {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                    <option value="Email">
                      📧 Email Agents - Email conversations with AI
                    </option>
                  )}
                  <option value="WhatsApp">
                    📱 WhatsApp Agents - WhatsApp messaging with AI
                  </option>
                </Select>

                {disabled ? (
                  <Text
                    fontSize="xs"
                    color="red.500"
                    mt={2}
                    fontWeight="medium"
                  >
                    Insufficient balance. Please add balance to create agents.
                  </Text>
                ) : (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    You'll be redirected to the creation page
                  </Text>
                )}
              </VStack>
            </>
          )}
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fit, minmax(520px, 1fr))" gap={6}>
          {allAgents.map(renderAgentCard)}
        </Grid>
      )}
    </Box>
  );
};

export default AgentsPage;
